/**
 * src/lib/cloudinary-signed.ts
 * 2004va — Signed Cloudinary URLs for venue-mode media.
 *
 * Calls the `cloudinary-sign` edge function. Caches signed URLs in memory
 * with TTL matching server-side expiry. Re-signs on demand when cache stale.
 *
 * Usage:
 *   const url = await getSignedUrl('the-room/abc123')
 *   const urls = await getSignedUrls(['the-room/abc', 'the-room/def'])
 *
 * The cache is intentionally module-level and in-memory only. No localStorage,
 * no IndexedDB — signed URLs are ephemeral by design and caching them across
 * browser restarts defeats the point.
 *
 * Security note: this module is the ONLY path by which venue-module UI should
 * access Cloudinary delivery URLs. Creator-side code still uses the raw
 * cloudinary_url from media_items rows for now (scheduled retrofit).
 */

import { supabase } from './supabase'

const SAFETY_MARGIN_SECONDS = 60  // refresh 60s before expiry

interface SignedUrlRecord {
  signed_url: string
  expires_at: number   // unix seconds
}

interface SignRequestItem {
  public_id:       string
  resource_type?:  'image' | 'video' | 'raw'
  transformations?: string
  /**
   * 'upload' for public assets (no signing needed, returns plain URL),
   * 'authenticated' for private assets (prefix-signed URL).
   * Defaults to 'authenticated' at the edge function to match the original
   * security-first behaviour for any caller that hasn't been updated.
   */
  delivery_type?:  'upload' | 'authenticated'
}

// Key format: `${resource_type}:${public_id}:${transformations ?? ''}:${delivery_type ?? 'authenticated'}`
// Different transforms or delivery types of the same asset cache separately.
const cache = new Map<string, SignedUrlRecord>()

function cacheKey(item: SignRequestItem): string {
  return `${item.resource_type ?? 'image'}:${item.public_id}:${item.transformations ?? ''}:${item.delivery_type ?? 'authenticated'}`
}

function now(): number {
  return Math.floor(Date.now() / 1000)
}

function isFresh(record: SignedUrlRecord): boolean {
  return record.expires_at - SAFETY_MARGIN_SECONDS > now()
}

/**
 * Get signed URLs for a batch of items. Returns in same order as input.
 * Cached URLs are served from cache. Fresh sign request made for any misses.
 */
export async function getSignedUrls(
  items: SignRequestItem[],
  opts: { expiresInSeconds?: number } = {},
): Promise<string[]> {
  if (items.length === 0) return []

  const needed:      SignRequestItem[] = []
  const neededIndex: number[]          = []

  // Pass 1: serve from cache where possible, collect misses
  const result: (string | undefined)[] = items.map((item, i) => {
    const key    = cacheKey(item)
    const cached = cache.get(key)
    if (cached && isFresh(cached)) return cached.signed_url
    needed.push(item)
    neededIndex.push(i)
    return undefined
  })

  if (needed.length === 0) {
    return result as string[]
  }

  // Pass 2: request the cache misses from the edge function
  const { data, error } = await supabase.functions.invoke<{
    signed: Array<{ public_id: string; signed_url: string; expires_at: string }>
    error?: string
  }>('cloudinary-sign', {
    body: {
      items: needed,
      expires_in_seconds: opts.expiresInSeconds,
    },
  })

  if (error) {
    throw new Error(`cloudinary-sign failed: ${error.message}`)
  }
  if (!data || data.error) {
    throw new Error(`cloudinary-sign returned error: ${data?.error ?? 'no response body'}`)
  }
  if (!Array.isArray(data.signed) || data.signed.length !== needed.length) {
    throw new Error('cloudinary-sign returned unexpected shape')
  }

  // Pass 3: write back to cache and result array
  data.signed.forEach((signedResponse, i) => {
    const item      = needed[i]
    const index     = neededIndex[i]
    const expiresAt = Math.floor(new Date(signedResponse.expires_at).getTime() / 1000)

    cache.set(cacheKey(item), {
      signed_url: signedResponse.signed_url,
      expires_at: expiresAt,
    })
    result[index] = signedResponse.signed_url
  })

  // Sanity: every slot should now be populated
  if (result.some(r => r === undefined)) {
    throw new Error('cloudinary-sign returned incomplete batch')
  }

  return result as string[]
}

/** Convenience wrapper for a single URL. */
export async function getSignedUrl(
  public_id:       string,
  opts: { resource_type?: 'image' | 'video' | 'raw'; transformations?: string; expiresInSeconds?: number } = {},
): Promise<string> {
  const [url] = await getSignedUrls(
    [{ public_id, resource_type: opts.resource_type, transformations: opts.transformations }],
    { expiresInSeconds: opts.expiresInSeconds },
  )
  return url
}

/**
 * Clear cached URLs. Useful when a picker logs out, or when a known bad
 * URL needs to be re-signed (e.g. Cloudinary config changed).
 */
export function clearSignedUrlCache(): void {
  cache.clear()
}

/**
 * Extract a Cloudinary public_id from a full Cloudinary URL.
 *
 * Cloudinary URLs look like:
 *   https://res.cloudinary.com/{cloud}/image/upload/v123456/the-room/abc123.jpg
 *   https://res.cloudinary.com/{cloud}/image/upload/w_800/the-room/abc123.jpg
 *   https://res.cloudinary.com/{cloud}/image/authenticated/s--xxx--/the-room/abc123.jpg
 *
 * We want: "the-room/abc123" (no transformations, no version, no extension)
 *
 * Returns null if the URL doesn't match expected Cloudinary shape. Caller
 * should handle null as "can't sign this — fall back to original URL or
 * surface an error."
 */
export function extractPublicId(cloudinaryUrl: string): string | null {
  try {
    const u = new URL(cloudinaryUrl)
    if (!u.hostname.endsWith('cloudinary.com')) return null

    // Path shape: /{cloud}/{resource_type}/{delivery_type}/[transformations/]
    // [version/] {public_id}.{ext}
    const parts = u.pathname.split('/').filter(Boolean)
    if (parts.length < 4) return null

    // Skip {cloud}/{resource_type}/{delivery_type}
    let idx = 3

    // Skip signed URL prefix if present
    if (parts[idx]?.startsWith('s--') && parts[idx]?.endsWith('--')) idx++

    // Skip transformation segment(s) — they contain commas or underscores with
    // known Cloudinary prefixes. Conservative: skip a segment if it matches
    // r'^[a-z]_' pattern (e.g. w_800, c_fit, q_auto).
    while (idx < parts.length && /^[a-z]_/.test(parts[idx])) {
      idx++
    }

    // Skip version segment (v + digits)
    if (parts[idx]?.match(/^v\d+$/)) idx++

    if (idx >= parts.length) return null

    const remaining = parts.slice(idx).join('/')
    // Strip file extension from last segment
    const publicId = remaining.replace(/\.[a-zA-Z0-9]+$/, '')

    return publicId || null
  } catch {
    return null
  }
}
